import { ChevronRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../../Shared/api';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Button } from '../../../Shared/UI/Button';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputDescription from '../../../Shared/UI/InputDescription';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { useRouter } from '../../router';

export default function AddDonationFormForm({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const { register, handleSubmit, setError, formState: { errors, defaultValues } } = useForm({
    defaultValues: {
      internal_title: '',
      title: '',
      enabled: false,
    },
  });

  const [addDonationForm, { loading, errors: serverErrors }] = useMutation(routes.add_donation_form(), {
    onSuccess: () => {
      notifier.notify(t('backend:donation_form_created'));
      onSuccess?.();
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <form onSubmit={handleSubmit(addDonationForm)}>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="py-1">
            {t('backend:add_a_donation_form')}
          </h3>
          <p className="text-slate-700">
            {t('backend:add_donation_form_intro')}
          </p>
        </div>

        <hr />

        {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

        <InputGroup>
          <InputLabel required valid={!errors.internal_title} htmlFor="create_donation_form_internal_title">
            {t('backend:internal_title')}
          </InputLabel>
          <InputDescription>
            {t('backend:donation_form_internal_title_description')}
          </InputDescription>
          <Input
            {...register('internal_title', { required: true })}
            id="create_donation_form_internal_title"
            className="mr-4"
            aria-invalid={!!errors.internal_title}
          />
          <ErrorMessage attribute={t('backend:internal_title')} error={errors.internal_title }/>
        </InputGroup>

        <InputGroup>
          <InputLabel required valid={!errors.title} htmlFor="create_donation_form_title">
            {t('backend:external_title')}
          </InputLabel>
          <InputDescription>
            {t('backend:donation_form_title_description')}
          </InputDescription>
          <Input
            {...register('title', { required: true })}
            id="create_donation_form_title"
            className="mr-4"
            aria-invalid={!!errors.title}
          />
          <ErrorMessage attribute={t('backend:external_title')} error={errors.title }/>
        </InputGroup>

        <hr />

        <div className="text-right">
          <Button
            type="submit"
            disabled={loading}
            loading={loading}
          >
            {t('backend:continue')}
            <Icon className="ml-1">
              <ChevronRight />
            </Icon>
          </Button>
        </div>
      </div>
    </form>
  );
}
